/*
 * TiersDashboard.tsx
 * Tableau de bord pour gérer un Tiers.
 * Permet l'affichage des informations principales, des adresses, des contacts
 * et des actions rapides.
 */

// Imports React
import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Skeleton } from '@radix-ui/themes';
import { Table } from '@radix-ui/themes';
import '@radix-ui/themes/styles.css';
import * as Tooltip from '@radix-ui/react-tooltip';

// Imports FontAwesome
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
	faPhone,
	faBriefcase,
	faPen,
	faTrash,
	faPlus,
	faIndustry,
	faEnvelope,
	faBuilding,
	faStar,
	faTruck,
	faFileContract,
	faAngleLeft,
	faBox,
} from '@fortawesome/free-solid-svg-icons';

// Imports communs
import {
	SecondaryButton,
	navigateBack,
	DefinitionTermField,
	DefinitionDescriptionField,
} from '../../../common';

// Imports Tiers
import { TiersData } from '../types/types';
import { getTiersById } from '../services/tiersApi';
import { isClient, isSupplier } from '../services/useTiers';

// Imports Adresses et Contacts
import { addressesApi } from 'features/addresses';
import { ContactCard, contactsApi } from 'features/contacts';

// Composant TiersDashboard
const TiersDashboard: React.FC = () => {
	// Hooks de State
	const [loading, setLoading] = useState(true);
	const [formData, setFormData] = React.useState<TiersData>();

	// Hook de Navigation
	const { id } = useParams<{ id: string }>();
	const navigate = useNavigate();

	// Fetch du Tiers et remplissage du formData
	React.useEffect(() => {
		if (id) {
			const token = localStorage.getItem('token');
			if (!token) return;
			getTiersById(Number(id), token)
				.then((data) => setFormData(data))
				.catch((error) => console.error(error))
				.finally(() => setLoading(false));
		}
	}, [id]);

	// Handlers pour les adresses (ajout, modification et suppression)
	const handleAddAddress = () =>
		navigate(
			'/app/addresses/new?tiersId=' +
				id +
				'&tiersLabel=' +
				encodeURIComponent(formData?.label || '')
		);
	const handleEditAddress = (id: number) =>
		navigate(`/app/addresses/${id}/edit`);
	const handleDeleteAddress = (id: number) => {
		const token = localStorage.getItem('token');
		if (!token) return;

		addressesApi
			.delete(id, token)
			.then(() => {
				setFormData((prev) =>
					prev
						? {
								...prev,
								addresses:
									prev.addresses?.filter((address) => address.id !== id) ??
									prev.addresses,
							}
						: prev
				);
			})
			.catch((error) => console.error(error));
	};

	// Handlers pour les contacts (ajout, modification et suppression)
	const handleAddContact = () =>
		navigate(
			'/app/contacts/new?tiersId=' +
				id +
				'&tiersLabel=' +
				encodeURIComponent(formData?.label || '')
		);
	const handleEditContact = (id: number) =>
		navigate(`/app/contacts/${id}/edit`);
	const handleDeleteContact = (id: number) => {
		const token = localStorage.getItem('token');
		if (!token) return;

		contactsApi
			.delete(id, token)
			.then(() => {
				setFormData((prev) =>
					prev
						? {
								...prev,
								contacts:
									prev.contacts?.filter((contact) => contact.id !== id) ??
									prev.contacts,
							}
						: prev
				);
			})
			.catch((error) => console.error(error));
	};

	// Rendu du composant
	return (
		<div>
			{/* Grille principale : 2 colonnes (informations principales à gauche, affaires et activités à droite) */}
			<div className="grid gap-0 grid-cols-1 md:grid-cols-[2fr_1fr]">
				{/* CARDS Informations principales */}
				<div className="mx-auto p-6 bg-white rounded-lg shadow-md min-w-full">
					{/* Ligne d'entête */}
					<div className="flex items-center mb-4">
						{/* Avatar rond avec icon faUserTie*/}
						{/* Test si le type de tiers est CLI */}
						{formData?.types && isClient(formData?.types) && !loading && (
							<div className="w-12 h-12 rounded-full bg-blue-600 flex items-center justify-center mr-4">
								<FontAwesomeIcon
									icon={faBuilding}
									className="w-7 h-7 text-gray-50"
								/>
							</div>
						)}
						{/* Sinon, Test si le type de tiers est FOU */}
						{formData?.types &&
							isSupplier(formData?.types) &&
							!isClient(formData?.types) &&
							!loading && (
								<div className="w-12 h-12 rounded-full bg-blue-600 flex items-center justify-center mr-4">
									<FontAwesomeIcon
										icon={faIndustry}
										className="w-7 h-7 pb-1 text-gray-50"
									/>
								</div>
							)}
						{/* Sinon, Placeholder pendant le chargement */}
						{loading && (
							<div className="w-12 h-12 rounded-full bg-gray-200 animate-pulse mr-4"></div>
						)}
						<h1 className="text-3xl font-bold mr-4">
							{loading ? (
								<Skeleton
									className="w-48 h-10 rounded block bg-gradient-to-r from-gray-200 via-gray-100 to-gray-200"
									style={{
										backgroundSize: '200% 100%',
										animation: 'shimmer 1.5s infinite linear',
									}}
								/>
							) : (
								formData?.label
							)}
						</h1>
						{/* Test si le type de tiers est CLI */}
						{formData?.types && isClient(formData?.types) && (
							<span className="px-2 py-0.5 rounded-full text-sm font-medium mx-2 bg-green-200 text-green-800">
								CLIENT
							</span>
						)}

						{/* Test si le type de tiers est FOU */}
						{formData?.types && isSupplier(formData?.types) && (
							<span
								className={`px-2 py-0.5 rounded-full text-sm font-medium mx-2 ${'bg-purple-200 text-purple-800'}`}
							>
								FOURNISSEUR
							</span>
						)}
					</div>

					{/* Placeholder pour les informations principales */}
					<div className="pt-4 pb-2">
						<p>
							<b>Informations principales : </b>
							<FontAwesomeIcon
								icon={faPen}
								className="w-4 h-4 text-gray-400 opacity-80 ml-2 cursor-pointer hover:text-gray-600"
								onClick={() => {
									let search = '';

									if (formData?.types) {
										if (
											isClient(formData.types) &&
											!isSupplier(formData.types)
										) {
											search = '?roles=client';
										} else if (
											isSupplier(formData.types) &&
											!isClient(formData.types)
										) {
											search = '?roles=fournisseur';
										} else if (
											isClient(formData.types) &&
											isSupplier(formData.types)
										) {
											search = '?roles=client,fournisseur';
										}
									}

									navigate({
										pathname: `/app/tiers/${id}/edit`,
										search,
									});
								}}
							/>
						</p>
						<div className="grid grid-cols-2 gap-0">
							{/* Labels à gauche, données à droite, les colonnes sont de largeur minimale */}
							<div>
								<dl className="grid grid-cols-[auto_1fr] gap-x-2 gap-y-1">
									<DefinitionTermField label="Adresse électronique de facturation :" />
									<DefinitionDescriptionField
										value={formData?.electronicInvoicingAddress || '—'}
										loading={loading}
									/>
									<DefinitionTermField label="SIREN :" />
									<DefinitionDescriptionField
										value={formData?.siren || '—'}
										loading={loading}
									/>
									<DefinitionTermField label="SIRET :" />
									<DefinitionDescriptionField
										value={formData?.siret || '—'}
										loading={loading}
									/>
									<DefinitionTermField label="NAF/APE :" />
									<DefinitionDescriptionField
										value={formData?.nafApe || '—'}
										loading={loading}
									/>
									<DefinitionTermField label="RCS :" />
									<DefinitionDescriptionField
										value={formData?.rcs || '—'}
										loading={loading}
									/>
									<DefinitionTermField label="TVA :" />
									<DefinitionDescriptionField
										value={formData?.vatNumber || '—'}
										loading={loading}
									/>
								</dl>
							</div>

							<div>
								<dl className="grid grid-cols-[auto_1fr] gap-x-2 gap-y-1">
									{/*<dt className="text-gray-500">Email :</dt>*/}
									<FontAwesomeIcon
										icon={faEnvelope}
										className="ml-2 mt-1 w-4 h-4 text-gray-300"
									/>
									{loading ? (
										<Skeleton
											className="w-48 h-6 rounded block bg-gradient-to-r from-gray-200 via-gray-100 to-gray-200"
											style={{
												backgroundSize: '200% 100%',
												animation: 'shimmer 1.5s infinite linear',
											}}
										/>
									) : (
										<dd className="text-gray-900">{formData?.email || '—'}</dd>
									)}

									{/*<dt className="text-gray-500">Téléphone :</dt>*/}
									<FontAwesomeIcon
										icon={faPhone}
										className="ml-2 mt-1 w-4 h-4 text-gray-300"
									/>
									{loading ? (
										<Skeleton
											className="w-48 h-6 rounded block bg-gradient-to-r from-gray-200 via-gray-100 to-gray-200"
											style={{
												backgroundSize: '200% 100%',
												animation: 'shimmer 1.5s infinite linear',
											}}
										/>
									) : (
										<dd className="text-gray-900">{formData?.phone || '—'}</dd>
									)}
								</dl>
							</div>
						</div>
					</div>
					<div className="pt-4 pb-2">
						<p>
							<b>Adresses</b>
							{/* Liste des adresses */}
							{/* Si il n'y a pas d'adresse, on propose d'en ajouter, sinon on affiche le tableau */}
							{!formData?.addresses?.length ? (
								<p className="text-gray-500">
									Aucune adresse.
									<FontAwesomeIcon
										icon={faPlus}
										className="ml-2 w-4 h-4 text-gray-300 hover:text-green-500 cursor-pointer"
										onClick={handleAddAddress}
									/>
								</p>
							) : (
								<Table.Root variant="ghost" layout="auto">
									<Table.Header>
										<Table.Row>
											<Table.ColumnHeaderCell className="text-gray-500 !font-normal"></Table.ColumnHeaderCell>
											<Table.ColumnHeaderCell className="text-gray-500 !font-normal">
												Nom
											</Table.ColumnHeaderCell>
											<Table.ColumnHeaderCell className="text-gray-500 !font-normal">
												Ligne 1
											</Table.ColumnHeaderCell>
											<Table.ColumnHeaderCell className="text-gray-500 !font-normal">
												Ligne 2
											</Table.ColumnHeaderCell>
											<Table.ColumnHeaderCell className="text-gray-500 !font-normal">
												CP
											</Table.ColumnHeaderCell>
											<Table.ColumnHeaderCell className="text-gray-500 !font-normal">
												Ville
											</Table.ColumnHeaderCell>
											<Table.ColumnHeaderCell className="text-gray-500 !font-normal">
												Pays
											</Table.ColumnHeaderCell>
											<Table.ColumnHeaderCell className="text-gray-500 !font-normal text-right">
												<FontAwesomeIcon
													icon={faPlus}
													className="ml-2 w-4 h-4 text-gray-300 hover:text-green-500 cursor-pointer"
													onClick={handleAddAddress}
												/>
											</Table.ColumnHeaderCell>
										</Table.Row>
									</Table.Header>
									<Table.Body>
										{formData?.addresses &&
											formData?.addresses.map((address) => (
												<Table.Row key={address.id}>
													<Table.RowHeaderCell justify="start">
														{/* Icones : étoile si principale, facure si devis, camion si livraison, survol souris indique le nom */}
														{address.types?.some(
															(type) => type.code === 'PRI'
														) && (
															<Tooltip.Provider delayDuration={200}>
																<Tooltip.Root>
																	<Tooltip.Trigger asChild>
																		<FontAwesomeIcon
																			icon={faStar}
																			className="mr-2 w-4 h-4 text-blue-500 cursor-pointer"
																		/>
																	</Tooltip.Trigger>

																	<Tooltip.Content className="bg-black text-white text-xs px-2 py-1 rounded shadow">
																		Adresse principale
																		<Tooltip.Arrow className="fill-black" />
																	</Tooltip.Content>
																</Tooltip.Root>
															</Tooltip.Provider>
														)}
														{address.types?.some(
															(type) => type.code === 'FAC'
														) && (
															<Tooltip.Provider delayDuration={200}>
																<Tooltip.Root>
																	<Tooltip.Trigger asChild>
																		<FontAwesomeIcon
																			icon={faFileContract}
																			className="mr-2 w-4 h-4 text-yellow-500 cursor-pointer"
																		/>
																	</Tooltip.Trigger>

																	<Tooltip.Content className="bg-black text-white text-xs px-2 py-1 rounded shadow">
																		Adresse de facturation
																		<Tooltip.Arrow className="fill-black" />
																	</Tooltip.Content>
																</Tooltip.Root>
															</Tooltip.Provider>
														)}
														{address.types?.some(
															(type) => type.code === 'LIV'
														) && (
															<Tooltip.Provider delayDuration={200}>
																<Tooltip.Root>
																	<Tooltip.Trigger asChild>
																		<FontAwesomeIcon
																			icon={faTruck}
																			className="mr-2 w-4 h-4 text-purple-500 cursor-pointer"
																		/>
																	</Tooltip.Trigger>

																	<Tooltip.Content className="bg-black text-white text-xs px-2 py-1 rounded shadow">
																		Adresse de livraison
																		<Tooltip.Arrow className="fill-black" />
																	</Tooltip.Content>
																</Tooltip.Root>
															</Tooltip.Provider>
														)}
													</Table.RowHeaderCell>
													<Table.Cell justify="start">
														{address.label}
													</Table.Cell>
													<Table.Cell>{address.line1}</Table.Cell>
													<Table.Cell>{address.line2}</Table.Cell>
													<Table.Cell>{address.zipCode}</Table.Cell>
													<Table.Cell>{address.city}</Table.Cell>
													<Table.Cell>{address.country}</Table.Cell>
													<Table.Cell className="text-gray-500 !font-normal text-right">
														<FontAwesomeIcon
															icon={faPen}
															className="ml-2 w-4 h-4 text-gray-300 hover:text-blue-500 cursor-pointer"
															onClick={() => handleEditAddress(address.id)}
														/>
														<FontAwesomeIcon
															icon={faTrash}
															className="ml-2 w-4 h-4 text-gray-300 hover:text-red-500 cursor-pointer"
															onClick={() => handleDeleteAddress(address.id)}
														/>
													</Table.Cell>
												</Table.Row>
											))}
									</Table.Body>
								</Table.Root>
							)}
						</p>
					</div>
					<div className="pt-4 pb-2">
						<p>
							<p className="mb-2">
								<b>Contacts</b>
							</p>
							{/* Liste des contacts */}
							<div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
								{formData?.contacts
									?.slice() // pour ne pas muter l'original
									.sort((a, b) => {
										// 1. Chargé d'affaire d'abord
										if (a.businessManager && !b.businessManager) return -1;
										if (!a.businessManager && b.businessManager) return 1;

										// 2. Prénom alphabétique
										const prenomComp = a.firstName.localeCompare(b.firstName);
										if (prenomComp !== 0) return prenomComp;

										// 3. Nom alphabétique
										return a.lastName.localeCompare(b.lastName);
									})
									.map((contact) => (
										<ContactCard
											key={contact.id}
											nom={contact.lastName}
											prenom={contact.firstName}
											telPro={contact.workPhone}
											telMobile={contact.mobilePhone}
											email={contact.email}
											poste={contact.position}
											chargeDAffaire={contact.businessManager}
											onEdit={() => handleEditContact(contact.id)}
											onDelete={() => handleDeleteContact(contact.id)}
										/>
									))}
								{/* Card pour ajouter un nouveau contact */}
								<div
									onClick={handleAddContact}
									className="flex flex-col justify-center items-center border-2 border-dashed border-gray-300 rounded-lg p-3 text-gray-400 cursor-pointer hover:border-green-500 hover:text-green-600 transition"
								>
									<FontAwesomeIcon icon={faPlus} className="text-xl mb-1" />
									<span className="text-sm font-semibold">
										Ajouter un contact
									</span>
								</div>
							</div>
						</p>
					</div>

					<div className="flex justify-end mt-4 space-x-2">
						<SecondaryButton
							onClick={() =>
								navigateBack(navigate, () => {
									const types = formData?.types;
									if (types && isSupplier(types) && !isClient(types)) {
										navigate('/app/suppliers');
										return;
									}
									navigate('/app/customers');
								})
							}
							label="Retour"
							size="lg"
							icon={faAngleLeft}
						/>
					</div>
				</div>

				<div className="mx-auto space-y-4">
					{formData?.types && isClient(formData?.types) && (
						<div className="mx-auto p-6 bg-white rounded-lg shadow-md">
							<h2 className="text-2xl font-bold mb-4">
								<FontAwesomeIcon
									icon={faFileContract}
									className="w-6 h-6 text-gray-700"
								/>{' '}
								Devis :
							</h2>
							{!formData?.quotes ||
								(formData?.quotes?.length === 0 && <p>Aucun devis.</p>)}
							{formData?.quotes && formData?.quotes?.length > 0 && (
								<>
									<p>
										<b>Derniers devis :</b>
									</p>
									<ul>
										{formData?.quotes?.slice(0, 5).map((devis) => (
											<li key={devis.id}>
												<a
													href={`/app/quotes/${devis.id}/manage`}
													className="hover:underline hover:text-blue-600 cursor-pointer"
												>
													{devis.referenceNumber}
												</a>
											</li>
										))}
									</ul>
								</>
							)}
						</div>
					)}

					{formData?.types && isSupplier(formData?.types) && (
						<div className="mx-auto p-6 bg-white rounded-lg shadow-md">
							<h2 className="text-2xl font-bold mb-4">
								<FontAwesomeIcon
									icon={faBox}
									className="w-6 h-6 text-gray-700"
								/>{' '}
								Commandes :
							</h2>
							<p>
								<b>Dernières commandes :</b>
							</p>
							<ul>
								{formData?.orders?.slice(0, 5).map((commande) => (
									<li key={commande.id}>
										<a href={`/app/orders/${commande.id}/manage`}>
											{commande.referenceNumber}
										</a>
									</li>
								))}
							</ul>
						</div>
					)}

					<div className="mx-auto p-6 bg-white rounded-lg shadow-md">
						<h2 className="text-2xl font-bold mb-4">
							<FontAwesomeIcon
								icon={faBriefcase}
								className="w-6 h-6 text-gray-700"
							/>{' '}
							Affaires :
						</h2>
						{!formData?.quotes ||
							(formData?.quotes?.length === 0 && <p>Aucun devis.</p>)}
						{formData?.quotes && formData?.quotes?.length > 0 && (
							<>
								<p>
									<b>Dernières affaires :</b>
								</p>
								<ul>
									{formData?.quotes?.slice(0, 5).map((devis) => (
										<li key={devis.project?.id}>
											<a
												href={`/app/projects/${devis.project?.id}/edit`}
												className="hover:underline hover:text-blue-600 cursor-pointer"
											>
												{devis.project?.referenceNumber}
											</a>
										</li>
									))}
								</ul>
							</>
						)}
					</div>
				</div>
			</div>
		</div>
	);
};
export default TiersDashboard;
